










































































































import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import IdGenerator from "@/lib/services/id-generator";
import ErrorConverterService from "@/modules/forms/error-converter.service";
import PlushieStore from "@/modules/plushie/store";
import Upgrade from "@/modules/plushie/upgrade.model";
import dataStore from "@/store";

import AllocationStatus from "../../allocation-workspace/AllocationStatus.vue";
import AllocationWorkspace from "../../allocation-workspace/AllocationWorkspace.vue";
import { protectAllocationDraft } from "../../allocation-workspace/protect-allocation-draft";
import {
  AllocationOrientation,
  AllocationDetailStatus,
  createUpgradeAllocationBaseline,
  materializeUpgradeAllocations,
  SparseAllocationDraft,
  upgradeAllocationKey,
} from "../../allocation-workspace/allocation-state";
import Factory from "../../factory.model";
import FactoryStore from "../../store";

@Component({
  components: {
    AllocationStatus,
    AllocationWorkspace,
    FormErrors,
    LoadingSpinner,
    SubmitButton,
  },
})
export default class UpgradeAllocationWorkspace extends Vue {
  @Prop({ default: false }) public readonly editable!: boolean;
  @Inject("IdGenerator") private fIdGenerator!: IdGenerator;
  @Inject("ErrorConverterService")
  private fErrorConverterService!: ErrorConverterService;

  private fFactoryDataStore: FactoryStore;
  private fPlushieDataStore: PlushieStore;
  private fState = new SparseAllocationDraft<boolean>({}, false);
  private fIsLoading = true;
  private fIsSaving = false;
  private fIsSaved = false;
  private fLoadErrors: string[] = [];
  private fSaveErrors: string[] = [];
  private fRemoveDraftGuard = (): void => undefined;

  constructor() {
    super();
    this.fFactoryDataStore = getModule(FactoryStore, dataStore);
    this.fPlushieDataStore = getModule(PlushieStore, dataStore);
  }

  get factories(): Factory[] {
    return this.fFactoryDataStore.activeFactoriesList;
  }

  get upgrades(): Upgrade[] {
    return this.fPlushieDataStore.upgradeListForFactories;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get isSaving(): boolean {
    return this.fIsSaving;
  }

  get isSaved(): boolean {
    return this.fIsSaved;
  }

  get loadErrors(): string[] {
    return this.fLoadErrors;
  }

  get saveErrors(): string[] {
    return this.fSaveErrors;
  }

  get isDirty(): boolean {
    return this.fState.isDirty;
  }

  get canSave(): boolean {
    return this.editable && this.isDirty && !this.isSaving;
  }

  get canDiscard(): boolean {
    return this.isDirty || this.fState.hasStagedDetails;
  }

  public getPairValue(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): boolean {
    return this.fState.getEffectiveValue(
      this.getPairKey(subjectId, detailId, orientation)
    );
  }

  public isPairConfigured(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): boolean {
    return this.fState.isConfigured(
      this.getPairKey(subjectId, detailId, orientation)
    );
  }

  public isPairModified(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): boolean {
    return this.fState.isModified(
      this.getPairKey(subjectId, detailId, orientation)
    );
  }

  public getDetailStatus(
    subject: Upgrade,
    detail: Factory,
    orientation: AllocationOrientation
  ): AllocationDetailStatus {
    if (!this.isPairConfigured(subject.id, detail.id, orientation)) {
      return "unconfigured";
    }

    return this.getPairValue(subject.id, detail.id, orientation)
      ? "enabled"
      : "disabled";
  }

  public canAddDetail(
    subject: Upgrade,
    detail: Factory,
    orientation: AllocationOrientation
  ): boolean {
    const key = this.getPairKey(subject.id, detail.id, orientation);
    return (
      !this.fState.getEffectiveValue(key) &&
      !this.fState.isStaged(key) &&
      !this.fState.isModified(key)
    );
  }

  public isStagedDetail(
    subject: Upgrade,
    detail: Factory,
    orientation: AllocationOrientation
  ): boolean {
    const key = this.getPairKey(subject.id, detail.id, orientation);
    return this.fState.isStaged(key);
  }

  public isModifiedDetail(
    subject: Upgrade,
    detail: Factory,
    orientation: AllocationOrientation
  ): boolean {
    return this.isPairModified(subject.id, detail.id, orientation);
  }

  public addCounterpart(
    subject: Upgrade,
    detail: Factory,
    orientation: AllocationOrientation
  ): void {
    if (
      !this.editable ||
      this.isSaving ||
      !this.canAddDetail(subject, detail, orientation)
    ) {
      return;
    }
    this.fState.setValue(
      this.getPairKey(subject.id, detail.id, orientation),
      true
    );
    this.fIsSaved = false;
    this.fSaveErrors = [];
  }

  public togglePairValue(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): void {
    if (!this.editable || this.isSaving) {
      return;
    }
    const key = this.getPairKey(subjectId, detailId, orientation);
    if (this.fState.getEffectiveValue(key)) {
      // Keep even a newly added pair visible as a pending removal until resolved.
      Vue.set(this.fState.drafts, key, false);
    } else {
      this.fState.setValue(key, true);
    }
    this.fIsSaved = false;
    this.fSaveErrors = [];
  }

  public async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }
    this.fIsSaving = true;
    this.fIsSaved = false;
    this.fSaveErrors = [];
    try {
      const payload = materializeUpgradeAllocations(
        this.upgrades,
        this.factories,
        this.fState,
        this.fIdGenerator
      );
      const saved = await this.fFactoryDataStore.saveUpgradeAllocations(
        payload
      );
      this.fState.replaceBaseline(createUpgradeAllocationBaseline(saved));
      this.fIsSaved = true;
    } catch (error) {
      this.fSaveErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsSaving = false;
    }
  }

  public discardChanges(): void {
    if (this.isSaving) {
      return;
    }
    this.fState.clearDrafts();
    this.fSaveErrors = [];
    this.fIsSaved = false;
  }

  public async loadData(): Promise<void> {
    this.fIsLoading = true;
    this.fLoadErrors = [];
    try {
      const [allocations] = await Promise.all([
        this.fFactoryDataStore.loadUpgradeAllocations(),
        this.fFactoryDataStore.loadFactories(),
        this.fPlushieDataStore.loadUpgrades(),
      ]);
      this.fState = new SparseAllocationDraft<boolean>(
        createUpgradeAllocationBaseline(allocations),
        false
      );
    } catch (error) {
      this.fLoadErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsLoading = false;
    }
  }

  protected created(): void {
    this.fRemoveDraftGuard = protectAllocationDraft(
      this.$router,
      () => this.isDirty,
      () => this.isSaving,
      () => this.$auth.check()
    );
    void this.loadData();
  }

  protected beforeDestroy(): void {
    this.fRemoveDraftGuard();
  }

  private getPairKey(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): string {
    const upgradeId = orientation === "item" ? subjectId : detailId;
    const factoryId = orientation === "item" ? detailId : subjectId;
    return upgradeAllocationKey(factoryId, upgradeId);
  }
}
